import API from '../config/api';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Image,
  Modal,
  InputGroup
} from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import CustomNavbar from '../components/CustomNavbar';
import ImageCropModal from '../components/ImageCropModal';

/*
  The fields that count toward profile completion. Driving the progress bar and
  the "what's missing" chips off one list keeps them from drifting apart.
*/
const COMPLETION_FIELDS = [
  { key: 'profileImage', icon: '📷', label: 'Profile Photo' },
  { key: 'name', icon: '🙍', label: 'Name' },
  { key: 'phone', icon: '📞', label: 'Phone' },
  { key: 'gender', icon: '⚧', label: 'Gender' },
  { key: 'address', icon: '🏠', label: 'Address' },
  { key: 'city', icon: '📍', label: 'City' },
  { key: 'state', icon: '🗺', label: 'State' },
  { key: 'pincode', icon: '🔢', label: 'Pincode' }
];

/* Motes of light drifting up through the banner. Purely decorative — the
   positions are hand-spread so they never rise in a visible column. */
const BANNER_SPARKS = [
  { glyph: '✦', x: '9%', y: '72%', size: '13px', dur: '7.5s', delay: '0s' },
  { glyph: '✧', x: '23%', y: '86%', size: '9px', dur: '9s', delay: '1.4s' },
  { glyph: '✦', x: '38%', y: '64%', size: '11px', dur: '8.2s', delay: '2.9s' },
  { glyph: '·', x: '52%', y: '90%', size: '18px', dur: '10s', delay: '0.7s' },
  { glyph: '✧', x: '67%', y: '70%', size: '12px', dur: '7.8s', delay: '3.6s' },
  { glyph: '✦', x: '79%', y: '88%', size: '10px', dur: '9.4s', delay: '2.1s' },
  { glyph: '✧', x: '91%', y: '66%', size: '14px', dur: '8.6s', delay: '4.8s' }
];

/* Account stat tiles — each links to the page it summarises */
const STAT_TILES = [
  {
    key: 'orders',
    icon: '📦',
    label: 'Orders',
    to: '/orders',
    c1: '#1d4ed8',
    c2: '#38bdf8',
    soft: '#eff6ff'
  },
  {
    key: 'wishlist',
    icon: '❤️',
    label: 'Wishlist',
    to: '/wishlist',
    c1: '#be123c',
    c2: '#fb7185',
    soft: '#fff1f2'
  },
  {
    key: 'cart',
    icon: '🛒',
    label: 'Cart Items',
    to: '/cart',
    c1: '#b45309',
    c2: '#fbbf24',
    soft: '#fffbeb'
  },
  {
    key: 'totalSpent',
    icon: '💰',
    label: 'Total Spent',
    to: '/orders',
    money: true,
    c1: '#15803d',
    c2: '#22c55e',
    soft: '#f0fdf4'
  }
];

/* Slices of the Buy Again shelf. A chip is only offered when it would
   actually match something, so the row never shows a dead end. */
const BUY_FILTERS = [
  { key: 'all', icon: '🛍', label: 'Everything' },
  { key: 'drops', icon: '💸', label: 'Price drops' },
  { key: 'favourites', icon: '⭐', label: 'Favourites' },
  { key: 'instock', icon: '⚡', label: 'In stock' }
];

const isPriceDrop = (item) =>
  typeof item.lastPricePaid === 'number' && item.price < item.lastPricePaid;

const matchesBuyFilter = (item, key) => {
  if (key === 'drops') return isPriceDrop(item);
  if (key === 'favourites') return (item.timesBought || 0) > 1;
  if (key === 'instock') return (item.stock ?? 0) > 0;
  return true;
};

/* Compact "how long ago" for the last-ordered line */
const timeAgo = (value) => {
  if (!value) return '';

  const then = new Date(value).getTime();

  if (Number.isNaN(then)) return '';

  const days = Math.floor((Date.now() - then) / 86400000);

  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

function ProfilePage() {
  const [user, setUser] = useState({});

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [imageFile, setImageFile] = useState(null);

  /* Crop-on-upload */
  const [cropSrc, setCropSrc] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  /* Tap-to-view avatar overview */
  const [showPreview, setShowPreview] = useState(false);

  const [buyAgainProducts, setBuyAgainProducts] = useState([]);

  /* Product currently being added to the cart from Buy Again */
  const [addingId, setAddingId] = useState(null);

  /* Per-card reorder quantity, and the card showing "✓ Added" right now */
  const [buyQty, setBuyQty] = useState({});
  const [addedId, setAddedId] = useState(null);

  /* Which slice of the Buy Again shelf is on screen */
  const [buyFilter, setBuyFilter] = useState('all');

  const [stats, setStats] = useState({});

  /* The "✓ Added" flash is on a timer — don't let it fire after unmount */
  const addedTimer = useRef(null);
  useEffect(() => () => clearTimeout(addedTimer.current), []);

  /* Managed preview URL for a freshly cropped file (revoked on change) */
  const [avatarSrc, setAvatarSrc] = useState(null);
  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setAvatarSrc(url);
      return () => URL.revokeObjectURL(url);
    }
    setAvatarSrc(user.profileImage || null);
  }, [imageFile, user.profileImage]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));

        const { data } = await axios.get(
          `${API}/users/profile`,

          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`
            }
          }
        );

        setUser(data);
        const statsResponse = await axios.get(
          `${API}/users/account-stats`,

          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`
            }
          }
        );

        setStats(statsResponse.data);
        const buyAgainResponse = await axios.get(
          `${API}/users/buy-again`,

          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`
            }
          }
        );

        setBuyAgainProducts(buyAgainResponse.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  /* A photo was picked → open the crop dialog instead of using it raw */
  const onSelectFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCropSrc(URL.createObjectURL(file));
    setShowCrop(true);
    e.target.value = ''; // let the same file be re-picked later
  };

  /*
    Crop applied → upload the photo and persist it to the profile RIGHT AWAY
    so it survives a page refresh (no need to also press "Save Changes").
  */
  const onCropDone = async (blob) => {
    const file = new File([blob], 'avatar.png', { type: 'image/png' });

    setImageFile(file); // instant local preview
    setShowCrop(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);

    try {
      setUploadingPhoto(true);

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      const formData = new FormData();
      formData.append('image', file);

      const { data } = await axios.post(
        `${API}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const imageUrl = data.image;

      await axios.put(
        `${API}/users/profile`,
        { profileImage: imageUrl },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );

      /* Use the persisted URL from now on so a refresh keeps the photo */
      setUser((prev) => ({ ...prev, profileImage: imageUrl }));
      setImageFile(null);

      toast.success('✅ Profile photo saved');
    } catch (error) {
      console.log(error);
      toast.error('Photo upload failed — click Save Changes to retry');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const cancelCrop = () => {
    setShowCrop(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const uploadImage = async () => {
    if (!imageFile) return user.profileImage;

    const formData = new FormData();

    formData.append('image', imageFile);

    const { data } = await axios.post(
      `${API}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    console.log('UPLOAD RESPONSE:', data);

    return data.image;
  };
  /* Reorder quantity per card. Defaults to 1 and never runs past stock —
     the cart endpoint would reject it anyway. */
  const qtyFor = (item) => buyQty[item.productId] || 1;

  const maxQtyFor = (item) => Math.max(1, Math.min(item.stock ?? 1, 10));

  const stepQty = (item, delta) => {
    setBuyQty((prev) => {
      const next = (prev[item.productId] || 1) + delta;

      return {
        ...prev,
        [item.productId]: Math.min(Math.max(next, 1), maxQtyFor(item))
      };
    });
  };

  /* Reorder straight from the profile instead of bouncing to the product
     page — the whole point of a Buy Again shelf. */
  const addToCartAgain = async (item) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));

    if (!userInfo) {
      toast.error('Please Login First');
      return;
    }

    const quantity = qtyFor(item);

    try {
      setAddingId(item.productId);

      const { status } = await axios.post(`${API}/cart`, {
        userId: userInfo._id,

        productId: item.productId,

        name: item.name,

        image: item.image,

        price: item.price,

        quantity
      });

      /* Keep the header cart count honest without a refetch. It counts
         DISTINCT products, so only a freshly created line (201) moves it —
         topping up something already in the cart (200) doesn't. */
      if (status === 201) {
        setStats((prev) => ({ ...prev, cart: (prev.cart || 0) + 1 }));
      }

      /* Confirm on the card itself, then quietly reset it */
      setAddedId(item.productId);
      clearTimeout(addedTimer.current);
      addedTimer.current = setTimeout(() => setAddedId(null), 2000);

      setBuyQty((prev) => ({ ...prev, [item.productId]: 1 }));

      toast.success(
        `🛒 ${item.name}${quantity > 1 ? ` ×${quantity}` : ''} added to cart`
      );
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || 'Failed to add to cart');
    } finally {
      setAddingId(null);
    }
  };

  const saveProfile = async () => {
    /* Same rules the checkout form enforces, so an address saved here can
       actually be used to place an order. */
    if (!String(user.name || '').trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (user.phone && !/^[0-9]{10}$/.test(String(user.phone).trim())) {
      toast.error('Phone number must be 10 digits');
      return;
    }

    if (user.pincode && !/^[0-9]{6}$/.test(String(user.pincode).trim())) {
      toast.error('Pincode must be 6 digits');
      return;
    }

    try {
      setSaving(true);

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      let imageUrl = user.profileImage;

      if (imageFile) {
        imageUrl = await uploadImage();
      }

      /* Send only what the profile endpoint accepts — no _id/isAdmin/token */
      const payload = {
        name: String(user.name || '').trim(),
        phone: String(user.phone || '').trim(),
        gender: user.gender || '',
        address: String(user.address || '').trim(),
        city: String(user.city || '').trim(),
        state: String(user.state || '').trim(),
        pincode: String(user.pincode || '').trim(),
        profileImage: imageUrl
      };

      await axios.put(
        `${API}/users/profile`,

        payload,

        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`
          }
        }
      );

      setUser({
        ...user,

        profileImage: imageUrl
      });

      toast.success('Profile Updated Successfully');
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || 'Update Failed');
    } finally {
      setSaving(false);
    }
  };
  const missingFields = COMPLETION_FIELDS.filter(
    (field) => !String(user[field.key] || '').trim()
  );

  const completedFields = COMPLETION_FIELDS.length - missingFields.length;

  const profileProgress = Math.round(
    (completedFields / COMPLETION_FIELDS.length) * 100
  );

  /* Items that got cheaper since the customer last bought them */
  const priceDropCount = buyAgainProducts.filter(isPriceDrop).length;

  /* What re-buying every dropped item would save, at one unit each */
  const totalSavings = buyAgainProducts.reduce(
    (sum, item) =>
      isPriceDrop(item) ? sum + (item.lastPricePaid - item.price) : sum,
    0
  );

  /* The single most-reordered product earns the crown */
  const topPickId = buyAgainProducts.reduce((best, item) => {
    if ((item.timesBought || 0) < 2) return best;

    const current = buyAgainProducts.find((p) => p.productId === best);

    return !current || item.timesBought > current.timesBought
      ? item.productId
      : best;
  }, null);

  const buyFilterCounts = BUY_FILTERS.reduce((acc, filter) => {
    acc[filter.key] = buyAgainProducts.filter((item) =>
      matchesBuyFilter(item, filter.key)
    ).length;

    return acc;
  }, {});

  const visibleBuyAgain = buyAgainProducts.filter((item) =>
    matchesBuyFilter(item, buyFilter)
  );

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric'
      })
    : null;
  return (
    <>
      <CustomNavbar />

      <div className="prof-wrap">
        <Container>
          {/* Profile banner */}
          <Card className="border-0 shadow-lg mb-4 prof-card overflow-hidden">
            <span className="prof-cover-blob prof-cover-blob-1" />
            <span className="prof-cover-blob prof-cover-blob-2" />

            {/* Divine light: slow rotating rays, a star field, a sweep of
                shine, and motes rising through it all. All decorative. */}
            <span className="prof-rays" aria-hidden="true" />
            <span className="prof-stars" aria-hidden="true" />
            <span className="prof-shine" aria-hidden="true" />

            {BANNER_SPARKS.map((spark, i) => (
              <span
                key={i}
                className="prof-spark"
                aria-hidden="true"
                style={{
                  '--x': spark.x,
                  '--y': spark.y,
                  '--s': spark.size,
                  '--d': spark.dur,
                  '--delay': spark.delay
                }}
              >
                {spark.glyph}
              </span>
            ))}

            <Card.Body className="position-relative p-4 p-md-5">
              <div className="d-flex flex-column flex-md-row align-items-center text-center text-md-start gap-3 gap-md-4">
                {/* Avatar */}
                <div className="prof-avatar-ring">
                  {/* Halo — a rotating ring of light behind the photo */}
                  <span className="prof-halo" aria-hidden="true" />
                  <span className="prof-halo-ring" aria-hidden="true" />

                  {avatarSrc ? (
                    <button
                      type="button"
                      className="prof-avatar-btn"
                      onClick={() => setShowPreview(true)}
                      title="View photo"
                    >
                      <Image
                        src={avatarSrc}
                        roundedCircle
                        className="prof-avatar"
                      />
                      <span className="prof-avatar-zoom">🔍</span>
                    </button>
                  ) : (
                    <div className="prof-avatar prof-avatar-initial">
                      {user.name ? user.name.charAt(0).toUpperCase() : '👤'}
                    </div>
                  )}
                  <label className="prof-cam-btn" title="Change photo">
                    📷
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={onSelectFile}
                    />
                  </label>

                  {uploadingPhoto && (
                    <div className="prof-avatar-uploading">
                      <div className="prof-avatar-spinner" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-grow-1">
                  <h2 className="fw-bold mb-1 prof-name">
                    {user.name || 'Your Name'}
                  </h2>
                  <p className="mb-2 prof-email">✉️ {user.email}</p>
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
                    {user.city && (
                      <span className="prof-chip">📍 {user.city}</span>
                    )}
                    {user.phone && (
                      <span className="prof-chip">📞 {user.phone}</span>
                    )}
                    {memberSince && (
                      <span className="prof-chip">🗓 Since {memberSince}</span>
                    )}
                    <span
                      className={`prof-chip${
                        profileProgress === 100 ? ' prof-chip-done' : ''
                      }`}
                    >
                      {profileProgress === 100 ? '✅' : '📈'} {profileProgress}%
                      complete
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Content */}
          <Card
            className="border-0 shadow-sm p-3 p-md-4 prof-content-card"
            style={{ borderRadius: '20px' }}
          >
            <Row className="g-3 mb-4">
              {STAT_TILES.map((tile) => {
                const value = stats[tile.key] || 0;

                return (
                  <Col xs={6} md={3} key={tile.key}>
                    {loading ? (
                      <Card className="border-0 shadow-sm p-3 prof-stat h-100">
                        <span className="prof-skel prof-skel-icon" />
                        <span className="prof-skel prof-skel-value" />
                        <span className="prof-skel prof-skel-label" />
                      </Card>
                    ) : (
                      <Card
                        as={Link}
                        to={tile.to}
                        className="border-0 shadow-sm p-3 prof-stat h-100 text-decoration-none"
                        style={{
                          '--c1': tile.c1,
                          '--c2': tile.c2,
                          '--soft': tile.soft
                        }}
                      >
                        <span className="prof-stat-accent" />

                        <span className="prof-stat-icon">{tile.icon}</span>

                        <span className="prof-stat-value">
                          {tile.money
                            ? `₹${Number(value).toLocaleString('en-IN')}`
                            : Number(value).toLocaleString('en-IN')}
                        </span>

                        <span className="prof-stat-label">{tile.label}</span>
                      </Card>
                    )}
                  </Col>
                );
              })}
            </Row>
            {/* Slim completion strip + shortcuts sharing the row */}
            <Row className="g-3 mb-4">
              <Col lg={5}>
                <div
                  className={`prof-mini${
                    profileProgress === 100 ? ' is-done' : ''
                  }`}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="prof-mini-label">
                      {profileProgress === 100 ? '🎉' : '📈'} Profile
                    </span>

                    <span className="prof-mini-pct">{profileProgress}%</span>

                    <span className="prof-mini-count">
                      {completedFields}/{COMPLETION_FIELDS.length}
                    </span>
                  </div>

                  <div className="prof-mini-track">
                    <div
                      className="prof-mini-fill"
                      style={{ width: `${profileProgress}%` }}
                    />
                  </div>

                  {profileProgress === 100 ? (
                    <small className="prof-mini-note done">
                      ✅ All set — your profile is complete
                    </small>
                  ) : (
                    <div className="prof-mini-chips">
                      {missingFields.slice(0, 3).map((field) => (
                        <span className="prof-mini-chip" key={field.key}>
                          {field.icon} {field.label}
                        </span>
                      ))}

                      {missingFields.length > 3 && (
                        <span className="prof-mini-chip prof-mini-more">
                          +{missingFields.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Col>

              {/* The space the old completion card used to waste */}
              <Col lg={7}>
                <div className="prof-quick">
                  <div className="prof-quick-head">⚡ Quick Actions</div>

                  <div className="prof-quick-grid">
                    {[
                      { to: '/orders', icon: '📦', label: 'Track Orders' },
                      { to: '/wishlist', icon: '❤️', label: 'Wishlist' },
                      { to: '/my-refunds', icon: '💰', label: 'My Refunds' },
                      { to: '/my-tickets', icon: '🎫', label: 'Support' },
                      { to: '/help', icon: '❓', label: 'Help' },
                      { to: '/products', icon: '🛍', label: 'Shop' }
                    ].map((action) => (
                      <Link
                        to={action.to}
                        className="prof-quick-tile"
                        key={action.to}
                      >
                        <span className="prof-quick-icon">{action.icon}</span>
                        <span className="prof-quick-label">{action.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            <Form className="prof-form">
              {/* ---- Basic details ---- */}
              <div className="prof-fieldset">
                <div className="prof-legend">
                  <span className="prof-legend-icon">🙍</span>
                  <div>
                    <div className="prof-legend-title">Basic Details</div>
                    <small className="prof-legend-hint">
                      How we address you and reach you
                    </small>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="prof-label">
                        Name <span className="prof-req">*</span>
                      </Form.Label>

                      <InputGroup className="prof-input">
                        <InputGroup.Text>🙍</InputGroup.Text>
                        <Form.Control
                          placeholder="Your full name"
                          value={user.name || ''}
                          onChange={(e) =>
                            setUser({ ...user, name: e.target.value })
                          }
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="prof-label">
                        Email
                        <span className="prof-locked">🔒 Can't be changed</span>
                      </Form.Label>

                      <InputGroup className="prof-input is-locked">
                        <InputGroup.Text>✉️</InputGroup.Text>
                        <Form.Control value={user.email || ''} readOnly />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="prof-label">Phone</Form.Label>

                      <InputGroup className="prof-input">
                        <InputGroup.Text>📞</InputGroup.Text>
                        <Form.Control
                          inputMode="numeric"
                          maxLength={10}
                          placeholder="10-digit mobile number"
                          value={user.phone || ''}
                          onChange={(e) =>
                            setUser({ ...user, phone: e.target.value })
                          }
                        />
                      </InputGroup>

                      <small className="prof-help">
                        Used for delivery updates
                      </small>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="prof-label">Gender</Form.Label>

                      <InputGroup className="prof-input">
                        <InputGroup.Text>⚧</InputGroup.Text>
                        <Form.Select
                          value={user.gender || ''}
                          onChange={(e) =>
                            setUser({ ...user, gender: e.target.value })
                          }
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </Form.Select>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* ---- Delivery address ---- */}
              <div className="prof-fieldset">
                <div className="prof-legend">
                  <span className="prof-legend-icon">📍</span>
                  <div>
                    <div className="prof-legend-title">Delivery Address</div>
                    <small className="prof-legend-hint">
                      Where your orders should arrive
                    </small>
                  </div>
                </div>

                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="prof-label">
                        Street Address
                      </Form.Label>

                      <Form.Control
                        as="textarea"
                        rows={3}
                        className="prof-textarea"
                        placeholder="House / flat no, street, landmark…"
                        value={user.address || ''}
                        onChange={(e) =>
                          setUser({ ...user, address: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="prof-label">City</Form.Label>

                      <InputGroup className="prof-input">
                        <InputGroup.Text>🏙</InputGroup.Text>
                        <Form.Control
                          placeholder="City"
                          value={user.city || ''}
                          onChange={(e) =>
                            setUser({ ...user, city: e.target.value })
                          }
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="prof-label">State</Form.Label>

                      <InputGroup className="prof-input">
                        <InputGroup.Text>🗺</InputGroup.Text>
                        <Form.Control
                          placeholder="State"
                          value={user.state || ''}
                          onChange={(e) =>
                            setUser({ ...user, state: e.target.value })
                          }
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="prof-label">Pincode</Form.Label>

                      <InputGroup className="prof-input">
                        <InputGroup.Text>🔢</InputGroup.Text>
                        <Form.Control
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="6 digits"
                          value={user.pincode || ''}
                          onChange={(e) =>
                            setUser({ ...user, pincode: e.target.value })
                          }
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* ---- Action bar ---- */}
              <div className="prof-actions">
                <small className="prof-actions-note">
                  {profileProgress === 100
                    ? '✅ Everything looks complete'
                    : `📈 ${missingFields.length} field${
                        missingFields.length === 1 ? '' : 's'
                      } left to complete your profile`}
                </small>

                <Button
                  size="lg"
                  className="fw-bold px-5 prof-save-btn"
                  /* Also locked while the profile is still loading — the form
                     renders empty until then, and a fast click would submit it. */
                  disabled={saving || loading}
                  onClick={saveProfile}
                >
                  {saving ? (
                    <>
                      <span className="prof-btn-spinner" /> Saving…
                    </>
                  ) : loading ? (
                    'Loading…'
                  ) : (
                    '💾 Save Changes'
                  )}
                </Button>
              </div>
            </Form>
          </Card>
          <Card className="border-0 shadow-sm mt-4 prof-buy-section">
            <Card.Body className="p-3 p-md-4">
              {/* Shelf header — the headline numbers double as the reason
                  to reorder (how much is on the table right now). */}
              <div className="prof-buy-hero mb-3">
                <span className="prof-buy-hero-glow prof-buy-hero-glow-1" />
                <span className="prof-buy-hero-glow prof-buy-hero-glow-2" />

                <div className="prof-buy-hero-inner">
                  <div className="d-flex align-items-center gap-3">
                    <span className="prof-buy-hero-icon">🔁</span>

                    <div>
                      <h4 className="prof-buy-hero-title mb-1">Buy Again</h4>
                      <p className="prof-buy-hero-sub mb-0">
                        {buyAgainProducts.length > 0
                          ? 'Your favourites — back in the cart in one tap'
                          : 'Everything you order lands here for a quick repeat'}
                      </p>
                    </div>
                  </div>

                  {buyAgainProducts.length > 0 && (
                    <div className="prof-buy-hero-stats">
                      <div className="prof-buy-stat">
                        <span className="prof-buy-stat-value">
                          {buyAgainProducts.length}
                        </span>
                        <span className="prof-buy-stat-label">
                          item{buyAgainProducts.length > 1 ? 's' : ''}
                        </span>
                      </div>

                      {priceDropCount > 0 && (
                        <div className="prof-buy-stat is-drop">
                          <span className="prof-buy-stat-value">
                            ↓ {priceDropCount}
                          </span>
                          <span className="prof-buy-stat-label">
                            price drop{priceDropCount > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}

                      {totalSavings > 0 && (
                        <div className="prof-buy-stat is-save">
                          <span className="prof-buy-stat-value">
                            ₹{Math.round(totalSavings).toLocaleString('en-IN')}
                          </span>
                          <span className="prof-buy-stat-label">to save</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Only worth offering once there's enough on the shelf to sift */}
              {!loading && buyAgainProducts.length > 2 && (
                <div className="prof-buy-filters mb-4">
                  {BUY_FILTERS.filter(
                    (filter) =>
                      filter.key === 'all' || buyFilterCounts[filter.key] > 0
                  ).map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      className={`prof-buy-filter${
                        buyFilter === filter.key ? ' is-active' : ''
                      }`}
                      onClick={() => setBuyFilter(filter.key)}
                    >
                      <span aria-hidden="true">{filter.icon}</span>{' '}
                      {filter.label}
                      <span className="prof-buy-filter-count">
                        {buyFilterCounts[filter.key]}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {loading ? (
                <Row className="g-3 g-md-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Col xl={3} lg={3} md={4} sm={6} xs={6} key={i}>
                      <Card className="h-100 border-0 shadow-sm prof-buy-card">
                        <span className="prof-skel prof-skel-img" />
                        <Card.Body className="p-3">
                          <span className="prof-skel prof-skel-line" />
                          <span className="prof-skel prof-skel-line short" />
                          <span className="prof-skel prof-skel-btn" />
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : buyAgainProducts.length === 0 ? (
                <div className="prof-buy-empty text-center">
                  <div className="prof-buy-empty-icon">🛍️</div>

                  <h6 className="fw-bold mb-1">Nothing to reorder yet</h6>

                  <p className="text-muted mb-3">
                    Once you place an order, every product lands here — with
                    price changes since you bought it.
                  </p>

                  <Link to="/" className="prof-buy-empty-btn">
                    Start shopping →
                  </Link>
                </div>
              ) : (
                <Row className="g-3 g-md-4">
                  {visibleBuyAgain.map((item, index) => {
                    const soldOut = (item.stock ?? 0) <= 0;

                    /* Compare today's price against what they actually paid */
                    const paid = item.lastPricePaid;

                    const diff =
                      typeof paid === 'number' ? item.price - paid : 0;

                    const cheaper = diff < 0;
                    const dearer = diff > 0;

                    const dropPct =
                      cheaper && paid > 0
                        ? Math.round((Math.abs(diff) / paid) * 100)
                        : 0;

                    const adding = addingId === item.productId;
                    const added = addedId === item.productId;

                    const isTopPick = item.productId === topPickId;

                    /* Nudge only when the shelf can still be emptied */
                    const lowStock = !soldOut && item.stock <= 5;

                    const qty = qtyFor(item);

                    return (
                      <Col
                        xl={3}
                        lg={3}
                        md={4}
                        sm={6}
                        xs={6}
                        key={item.productId}
                      >
                        <Card
                          className={`h-100 border-0 prof-buy-card${
                            soldOut ? ' is-out' : ''
                          }${cheaper ? ' is-deal' : ''}${
                            added ? ' is-added' : ''
                          }`}
                          /* Drives the staggered entrance of the shelf */
                          style={{ '--i': index }}
                        >
                          <span className="prof-buy-card-accent" />

                          <div className="prof-buy-img-wrap">
                            <div className="prof-buy-flags">
                              {isTopPick && (
                                <span className="prof-buy-flag prof-buy-flag-top">
                                  👑 Your top pick
                                </span>
                              )}

                              <span className="prof-buy-flag">
                                🔁 Bought
                                {item.timesBought > 1
                                  ? ` ${item.timesBought}×`
                                  : ''}
                              </span>
                            </div>

                            {cheaper && (
                              <span className="prof-buy-drop">
                                ↓ ₹{Math.abs(diff).toLocaleString('en-IN')}
                                {dropPct > 0 ? ` · ${dropPct}%` : ''}
                              </span>
                            )}

                            <Card.Img
                              variant="top"
                              src={
                                item.image ||
                                'https://placehold.co/300x200?text=No+Image'
                              }
                              alt=""
                              className="prof-buy-img"
                              onError={(e) => {
                                e.currentTarget.src =
                                  'https://placehold.co/300x200?text=No+Image';
                              }}
                            />

                            <span className="prof-buy-sheen" />

                            {soldOut && (
                              <span className="prof-buy-out">Out of Stock</span>
                            )}

                            <Link
                              to={`/product/${item.productId}`}
                              className="prof-buy-overlay"
                            >
                              <span className="prof-buy-overlay-btn">
                                <FaEye className="me-1" /> View details
                              </span>
                            </Link>
                          </div>

                          <Card.Body className="d-flex flex-column p-3">
                            <h6 className="fw-bold prof-buy-name mb-1">
                              {item.name}
                            </h6>

                            <div className="prof-buy-meta mb-2">
                              {item.lastOrderedAt
                                ? `Last ordered ${timeAgo(item.lastOrderedAt)}`
                                : 'Previously ordered'}
                            </div>

                            <div className="d-flex align-items-baseline gap-2 mb-1">
                              <span className="fw-bold prof-buy-price">
                                ₹{Number(item.price).toLocaleString('en-IN')}
                              </span>

                              {(cheaper || dearer) && (
                                <small className="text-muted text-decoration-line-through">
                                  ₹{Number(paid).toLocaleString('en-IN')}
                                </small>
                              )}
                            </div>

                            {/* Real price movement since their last purchase */}
                            <div className="prof-buy-delta mb-2">
                              {cheaper && (
                                <span className="prof-buy-save-pill">
                                  🎉 You save ₹
                                  {Math.abs(diff).toLocaleString('en-IN')}
                                </span>
                              )}
                              {dearer && (
                                <span className="is-up">
                                  ₹{diff.toLocaleString('en-IN')} more than you
                                  paid
                                </span>
                              )}
                              {!cheaper && !dearer && (
                                <span className="is-same">
                                  Same price as last time
                                </span>
                              )}
                            </div>

                            {lowStock && (
                              <div className="prof-buy-stock mb-2">
                                ⚠️ Only {item.stock} left
                              </div>
                            )}

                            <div className="prof-buy-actions mt-auto">
                              {!soldOut && (
                                <div
                                  className="prof-buy-qty"
                                  aria-label="Reorder quantity"
                                >
                                  <button
                                    type="button"
                                    onClick={() => stepQty(item, -1)}
                                    disabled={qty <= 1 || adding}
                                    aria-label="Decrease quantity"
                                  >
                                    −
                                  </button>

                                  <span className="prof-buy-qty-value">
                                    {qty}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() => stepQty(item, 1)}
                                    disabled={qty >= maxQtyFor(item) || adding}
                                    aria-label="Increase quantity"
                                  >
                                    +
                                  </button>
                                </div>
                              )}

                              <Button
                                size="sm"
                                className={`fw-semibold prof-buy-btn${
                                  added ? ' is-added' : ''
                                }`}
                                disabled={soldOut || adding}
                                onClick={() => addToCartAgain(item)}
                              >
                                {soldOut ? (
                                  'Out of Stock'
                                ) : adding ? (
                                  <>
                                    <span className="prof-btn-spinner" />{' '}
                                    Adding…
                                  </>
                                ) : added ? (
                                  '✓ Added'
                                ) : (
                                  '🛒 Add to Cart'
                                )}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Crop dialog (after picking a new photo) */}
      <ImageCropModal
        show={showCrop}
        imageSrc={cropSrc}
        onCancel={cancelCrop}
        onCropDone={onCropDone}
      />

      {/* Full-size photo overview */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        centered
        contentClassName="prof-preview-modal"
      >
        <Modal.Body className="p-0 text-center position-relative">
          <button
            type="button"
            className="prof-preview-close"
            onClick={() => setShowPreview(false)}
            aria-label="Close"
          >
            ✕
          </button>
          {avatarSrc && (
            <img src={avatarSrc} alt="Profile" className="prof-preview-img" />
          )}
        </Modal.Body>
      </Modal>

      <style>{profileStyles}</style>
    </>
  );
}

const profileStyles = `
  /* ===== Celestial page canvas ===== */
  .prof-wrap {
    position: relative;
    overflow: hidden;
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }
  /* Aurora that breathes slowly behind everything */
  .prof-wrap::before {
    content: '';
    position: absolute;
    inset: -18% -12%;
    pointer-events: none;
    background:
      radial-gradient(420px 280px at 18% 14%, rgba(251,191,36,0.22), transparent 66%),
      radial-gradient(480px 320px at 82% 40%, rgba(168,85,247,0.18), transparent 66%),
      radial-gradient(400px 260px at 46% 86%, rgba(56,189,248,0.16), transparent 66%);
    filter: blur(8px);
    animation: profAurora 24s ease-in-out infinite alternate;
  }
  @keyframes profAurora {
    0%   { transform: translate3d(-2%, -1%, 0) scale(1);    opacity: 0.85; }
    50%  { transform: translate3d(2%, 2%, 0)   scale(1.08); opacity: 1; }
    100% { transform: translate3d(-1%, 3%, 0)  scale(1.03); opacity: 0.9; }
  }
  /* Faint stardust drifting upward across the whole page */
  .prof-wrap::after {
    content: '';
    position: absolute;
    inset: -50% 0 0 0;
    pointer-events: none;
    opacity: 0.5;
    background-image:
      radial-gradient(1.5px 1.5px at 12% 18%, rgba(251,191,36,0.85), transparent),
      radial-gradient(1.5px 1.5px at 68% 8%,  rgba(168,85,247,0.7), transparent),
      radial-gradient(2px 2px   at 34% 42%,   rgba(255,255,255,0.9), transparent),
      radial-gradient(1.5px 1.5px at 88% 34%, rgba(234,88,12,0.6), transparent),
      radial-gradient(1.5px 1.5px at 52% 66%, rgba(251,191,36,0.7), transparent),
      radial-gradient(2px 2px   at 8% 78%,    rgba(168,85,247,0.6), transparent),
      radial-gradient(1.5px 1.5px at 76% 88%, rgba(255,255,255,0.9), transparent);
    background-size: 100% 50%;
    animation: profDust 70s linear infinite;
  }
  @keyframes profDust {
    from { background-position: 0 0; }
    to   { background-position: 0 -50%; }
  }
  /* Keep the actual page content above the celestial layers */
  .prof-wrap > * {
    position: relative;
    z-index: 1;
  }

  /* ===== Banner ===== */
  .prof-card {
    border-radius: 26px;
    color: #fff;
    background: linear-gradient(125deg, #1e1b4b 0%, #4c1d95 26%, #7c2d12 56%, #ea580c 78%, #fbbf24 100%);
    background-size: 260% 260%;
    animation: profDrift 16s ease-in-out infinite, profDescend 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
    position: relative;
    isolation: isolate;
    box-shadow: 0 26px 60px rgba(76,29,149,0.34), 0 10px 26px rgba(234,88,12,0.22) !important;
  }
  @keyframes profDrift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  /* The banner settles in from above on first paint */
  @keyframes profDescend {
    from { opacity: 0; transform: translateY(-18px) scale(0.985); }
    to   { opacity: 1; transform: none; }
  }
  /* Luminous rim that travels around the edge */
  .prof-card::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 4;
    border-radius: inherit;
    padding: 1.5px;
    background: linear-gradient(120deg, rgba(255,255,255,0.75), rgba(251,191,36,0.85) 25%, rgba(255,255,255,0.12) 45%, rgba(167,139,250,0.8) 70%, rgba(255,255,255,0.7));
    background-size: 300% 300%;
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    animation: profDrift 9s ease-in-out infinite;
    pointer-events: none;
  }
  .prof-cover-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.5;
    pointer-events: none;
    animation: profBlobDrift 14s ease-in-out infinite alternate;
  }
  .prof-cover-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -90px; left: 18%;
  }
  .prof-cover-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.35;
    animation-duration: 18s;
    animation-direction: alternate-reverse;
  }
  @keyframes profBlobDrift {
    from { transform: translate3d(-14px, 6px, 0) scale(1); }
    to   { transform: translate3d(18px, -10px, 0) scale(1.18); }
  }

  /* Rotating shafts of light from the centre of the banner */
  .prof-rays {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 170%;
    aspect-ratio: 1 / 1;
    transform: translate(-50%, -50%);
    transform-origin: center;
    pointer-events: none;
    opacity: 0.55;
    background: conic-gradient(from 0deg,
      rgba(255,255,255,0.20) 0deg 5deg, transparent 5deg 24deg,
      rgba(251,191,36,0.22) 24deg 30deg, transparent 30deg 52deg,
      rgba(255,255,255,0.14) 52deg 57deg, transparent 57deg 90deg);
    -webkit-mask: radial-gradient(closest-side, rgba(0,0,0,0.95), rgba(0,0,0,0.35) 55%, transparent 80%);
    mask: radial-gradient(closest-side, rgba(0,0,0,0.95), rgba(0,0,0,0.35) 55%, transparent 80%);
    animation: profRaySpin 46s linear infinite;
  }
  @keyframes profRaySpin {
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }

  /* Star field that twinkles in and out */
  .prof-stars {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      radial-gradient(1.6px 1.6px at 14% 26%, rgba(255,255,255,0.95), transparent),
      radial-gradient(1.2px 1.2px at 27% 68%, rgba(255,255,255,0.75), transparent),
      radial-gradient(2px 2px     at 41% 18%, rgba(253,230,138,0.95), transparent),
      radial-gradient(1.3px 1.3px at 58% 52%, rgba(255,255,255,0.8), transparent),
      radial-gradient(1.7px 1.7px at 71% 24%, rgba(255,255,255,0.9), transparent),
      radial-gradient(1.2px 1.2px at 84% 62%, rgba(253,230,138,0.85), transparent),
      radial-gradient(1.5px 1.5px at 93% 34%, rgba(255,255,255,0.8), transparent),
      radial-gradient(1.2px 1.2px at 47% 82%, rgba(255,255,255,0.7), transparent);
    animation: profTwinkle 4.6s ease-in-out infinite alternate;
  }
  @keyframes profTwinkle {
    from { opacity: 0.35; }
    to   { opacity: 1; }
  }

  /* A blade of light sweeping across the banner */
  .prof-shine {
    position: absolute;
    top: -60%;
    bottom: -60%;
    left: -35%;
    width: 26%;
    pointer-events: none;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.34), transparent);
    filter: blur(7px);
    transform: rotate(14deg);
    animation: profShine 8s ease-in-out infinite;
  }
  @keyframes profShine {
    0%        { left: -35%; opacity: 0; }
    10%       { opacity: 1; }
    45%       { opacity: 1; }
    55%, 100% { left: 118%; opacity: 0; }
  }

  /* Motes of light rising through the banner */
  .prof-spark {
    position: absolute;
    top: var(--y);
    left: var(--x);
    font-size: var(--s);
    line-height: 1;
    color: rgba(255,255,255,0.95);
    text-shadow: 0 0 12px rgba(251,191,36,0.95), 0 0 22px rgba(255,255,255,0.5);
    pointer-events: none;
    opacity: 0;
    animation: profRise var(--d) ease-in-out var(--delay) infinite;
  }
  @keyframes profRise {
    0%   { opacity: 0;    transform: translateY(16px) scale(0.5) rotate(0deg); }
    18%  { opacity: 0.95; }
    72%  { opacity: 0.55; }
    100% { opacity: 0;    transform: translateY(-62px) scale(1.2) rotate(200deg); }
  }

  .prof-avatar-ring {
    position: relative;
    flex-shrink: 0;
    padding: 5px;
    border-radius: 50%;
    background: rgba(255,255,255,0.25);
    backdrop-filter: blur(4px);
    box-shadow: 0 0 0 1px rgba(255,255,255,0.4), 0 0 38px rgba(251,191,36,0.5);
    animation: profHaloPulse 4.8s ease-in-out infinite;
  }
  @keyframes profHaloPulse {
    0%, 100% { box-shadow: 0 0 0 1px rgba(255,255,255,0.4), 0 0 26px rgba(251,191,36,0.4); }
    50%      { box-shadow: 0 0 0 1px rgba(255,255,255,0.6), 0 0 52px rgba(251,191,36,0.75); }
  }
  /* Soft rotating aura */
  .prof-halo {
    position: absolute;
    inset: -14px;
    z-index: 0;
    border-radius: 50%;
    pointer-events: none;
    background: conic-gradient(from 0deg, rgba(251,191,36,0), rgba(251,191,36,0.9) 20%, rgba(255,255,255,0.95) 40%, rgba(167,139,250,0.85) 65%, rgba(56,189,248,0.6) 82%, rgba(251,191,36,0));
    filter: blur(9px);
    opacity: 0.8;
    animation: profSpin 14s linear infinite;
  }
  /* Crisp halo ring that breathes outward */
  .prof-halo-ring {
    position: absolute;
    inset: -9px;
    z-index: 0;
    border-radius: 50%;
    pointer-events: none;
    border: 1px solid rgba(255,255,255,0.55);
    animation: profHaloRing 3.6s ease-out infinite;
  }
  @keyframes profHaloRing {
    0%   { transform: scale(0.94); opacity: 0.85; }
    70%  { transform: scale(1.16); opacity: 0; }
    100% { transform: scale(1.16); opacity: 0; }
  }
  /* Photo and its controls sit above the aura */
  .prof-avatar-btn,
  .prof-avatar-initial,
  .prof-cam-btn,
  .prof-avatar-uploading {
    z-index: 2;
  }
  .prof-avatar {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 50%;
    border: 3px solid #fff;
    background: #fff;
    display: block;
  }
  .prof-avatar-initial {
    position: relative;
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 3px solid #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    font-size: 3rem;
    font-weight: 800;
    line-height: 1;
    text-transform: uppercase;
  }
  /* Clickable avatar → opens the full-size overview */
  .prof-avatar-btn {
    position: relative;
    display: block;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
  }
  .prof-avatar-zoom {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(15,23,42,0.45);
    color: #fff;
    font-size: 1.6rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  .prof-avatar-btn:hover .prof-avatar-zoom {
    opacity: 1;
  }
  .prof-avatar-uploading {
    position: absolute;
    inset: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(15,23,42,0.5);
  }
  .prof-avatar-spinner {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 3px solid rgba(255,255,255,0.35);
    border-top-color: #fff;
    animation: profSpin 0.8s linear infinite;
  }
  @keyframes profSpin {
    to { transform: rotate(360deg); }
  }

  /* Photo overview modal */
  .prof-preview-modal {
    background: transparent;
    border: none;
  }
  .prof-preview-img {
    max-width: 100%;
    max-height: 82vh;
    /* drop-shadow follows the PNG's circular alpha (no boxy shadow) */
    filter: drop-shadow(0 16px 40px rgba(0,0,0,0.5));
  }
  .prof-preview-close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    width: 38px;
    height: 38px;
    border: none;
    border-radius: 50%;
    background: rgba(0,0,0,0.55);
    color: #fff;
    font-size: 1.1rem;
    cursor: pointer;
  }
  .prof-preview-close:hover {
    background: rgba(0,0,0,0.75);
  }
  .prof-cam-btn {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #fbbf24;
    border: 2px solid #fff;
    cursor: pointer;
    font-size: 0.95rem;
    box-shadow: 0 4px 10px rgba(0,0,0,0.25);
    transition: transform 0.2s ease;
  }
  .prof-cam-btn:hover {
    transform: scale(1.12);
  }
  /* Gilded name — light travels through the letters */
  .prof-name {
    font-size: clamp(1.55rem, 4.2vw, 2.25rem);
    letter-spacing: -0.5px;
    background: linear-gradient(100deg, #ffffff 8%, #fde68a 26%, #ffffff 44%, #fcd34d 64%, #ffffff 88%);
    background-size: 260% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 2px 14px rgba(251,191,36,0.45));
    animation: profGild 7s linear infinite;
  }
  @keyframes profGild {
    from { background-position: 260% 0; }
    to   { background-position: -60% 0; }
  }
  .prof-email {
    opacity: 0.85;
    word-break: break-word;
    text-shadow: 0 1px 10px rgba(0,0,0,0.25);
  }
  .prof-chip {
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 30px;
    padding: 5px 14px;
    font-size: 0.82rem;
    font-weight: 500;
    backdrop-filter: blur(4px);
    box-shadow: 0 2px 12px rgba(0,0,0,0.12);
    transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .prof-chip:hover {
    transform: translateY(-3px);
    background: rgba(255,255,255,0.3);
    border-color: rgba(253,230,138,0.75);
    box-shadow: 0 10px 22px rgba(251,191,36,0.35), 0 0 18px rgba(255,255,255,0.28);
  }
  .prof-chip-done {
    background: rgba(34,197,94,0.35);
    border-color: rgba(255,255,255,0.45);
    font-weight: 700;
    animation: profChipGlow 3.2s ease-in-out infinite;
  }
  @keyframes profChipGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(74,222,128,0.5); }
    50%      { box-shadow: 0 0 18px 3px rgba(74,222,128,0.45); }
  }

  /* Panels below the banner — panes of soft light, easing in behind it */
  .prof-content-card {
    position: relative;
    background: linear-gradient(180deg, rgba(255,255,255,0.97), rgba(255,251,245,0.97));
    backdrop-filter: blur(6px);
    box-shadow: 0 18px 44px rgba(124,45,18,0.10) !important;
    animation: profDescend 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
  }
  .prof-buy-section {
    animation: profDescend 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.22s both;
  }

  /* Stat tiles */
  .prof-stat {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    color: inherit;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  a.prof-stat:hover { color: inherit; }
  .prof-stat::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--soft);
    opacity: 0.5;
    transition: opacity 0.25s ease;
    pointer-events: none;
  }
  .prof-stat::after {
    content: '';
    position: absolute;
    top: -40px;
    right: -40px;
    width: 104px;
    height: 104px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--c1), var(--c2));
    opacity: 0.12;
    transition: transform 0.35s ease;
    pointer-events: none;
  }
  .prof-stat:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 30px rgba(194, 65, 12,0.16) !important;
  }
  .prof-stat:hover::before { opacity: 0.92; }
  .prof-stat:hover::after  { transform: scale(1.25); }
  .prof-stat-accent {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    z-index: 2;
    background: linear-gradient(180deg, var(--c1), var(--c2));
  }
  .prof-stat-icon,
  .prof-stat-value,
  .prof-stat-label {
    position: relative;
    z-index: 1;
    display: block;
  }
  .prof-stat-icon {
    font-size: 1.6rem;
    line-height: 1;
    margin-bottom: 8px;
  }
  .prof-stat-value {
    font-weight: 800;
    font-size: clamp(1.05rem, 2.2vw, 1.5rem);
    line-height: 1.15;
    letter-spacing: -0.4px;
    word-break: break-word;
    background: linear-gradient(120deg, var(--c1), var(--c2));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .prof-stat-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #78716c;
    margin-top: 2px;
  }

  /* Skeletons */
  .prof-skel {
    display: block;
    background: linear-gradient(90deg, #f1ece4 25%, #faf7f2 37%, #f1ece4 63%);
    background-size: 400% 100%;
    animation: profShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .prof-skel-icon  { width: 34px; height: 34px; border-radius: 10px; margin-bottom: 10px; }
  .prof-skel-value { width: 60%; height: 22px; margin-bottom: 8px; }
  .prof-skel-label { width: 45%; height: 11px; }
  .prof-skel-img   { width: 100%; height: clamp(140px, 18vw, 180px); border-radius: 0; }
  .prof-skel-line  { width: 80%; height: 12px; margin-bottom: 8px; }
  .prof-skel-line.short { width: 50%; }
  .prof-skel-btn   { width: 100%; height: 32px; border-radius: 10px; margin-top: 10px; }
  @keyframes profShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  /* Spinner inside the save button */
  .prof-btn-spinner {
    display: inline-block;
    width: 15px;
    height: 15px;
    margin-right: 8px;
    vertical-align: -2px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    animation: profSpin 0.8s linear infinite;
  }

  .prof-section-title {
    border-left: 4px solid #ea580c;
    padding-left: 10px;
  }

  /* ===== Slim completion strip ===== */
  .prof-mini {
    height: 100%;
    border-radius: 14px;
    padding: 12px 14px;
    background: linear-gradient(135deg, #fffbeb, #fff7ed);
    border: 1px solid #fde68a;
  }
  .prof-mini.is-done {
    background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
    border-color: #bbf7d0;
  }
  .prof-mini-label {
    font-size: 0.78rem;
    font-weight: 800;
    color: #92400e;
  }
  .prof-mini.is-done .prof-mini-label { color: #15803d; }
  .prof-mini-pct {
    font-size: 0.95rem;
    font-weight: 800;
    color: #b45309;
    font-variant-numeric: tabular-nums;
  }
  .prof-mini.is-done .prof-mini-pct { color: #15803d; }
  .prof-mini-count {
    margin-left: auto;
    font-size: 0.68rem;
    font-weight: 700;
    color: #a8a29e;
    font-variant-numeric: tabular-nums;
  }
  /* deliberately thin — this used to be a full-height card */
  .prof-mini-track {
    height: 6px;
    border-radius: 6px;
    background: rgba(120, 53, 15, 0.12);
    overflow: hidden;
  }
  .prof-mini-fill {
    height: 100%;
    border-radius: 6px;
    background: linear-gradient(90deg, #f59e0b, #fb923c);
    transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .prof-mini.is-done .prof-mini-fill {
    background: linear-gradient(90deg, #16a34a, #22c55e);
  }
  .prof-mini-note.done {
    display: block;
    margin-top: 8px;
    font-size: 0.74rem;
    font-weight: 700;
    color: #15803d;
  }
  .prof-mini-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 9px;
  }
  .prof-mini-chip {
    background: #fff;
    border: 1px solid #fde68a;
    color: #b45309;
    border-radius: 20px;
    padding: 3px 9px;
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .prof-mini-more {
    background: #fef3c7;
    border-color: #fcd34d;
  }

  /* ===== Quick actions (fills the freed space) ===== */
  .prof-quick {
    height: 100%;
    border-radius: 14px;
    padding: 12px 14px;
    background: #fdfcfa;
    border: 1px solid #f1e4d0;
  }
  .prof-quick-head {
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #a8a29e;
    margin-bottom: 10px;
  }
  .prof-quick-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
    gap: 8px;
  }
  .prof-quick-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 10px 6px;
    border-radius: 12px;
    text-decoration: none;
    color: #7c2d12;
    background: #fff;
    border: 1px solid #f1e4d0;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .prof-quick-tile:hover {
    transform: translateY(-3px);
    background: #fff7ed;
    border-color: #fed7aa;
    box-shadow: 0 10px 20px rgba(234, 88, 12, 0.16);
    color: #7c2d12;
  }
  .prof-quick-icon { font-size: 1.3rem; line-height: 1; }
  .prof-quick-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-align: center;
  }

  /* ===== Form ===== */
  .prof-fieldset {
    border: 1px solid #f1e4d0;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 18px;
    background: #fffdfa;
  }
  .prof-legend {
    display: flex;
    align-items: center;
    gap: 11px;
    padding-bottom: 12px;
    margin-bottom: 14px;
    border-bottom: 1px dashed #f1e4d0;
  }
  .prof-legend-icon {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    border-radius: 12px;
    background: linear-gradient(135deg, #fff7ed, #ffedd5);
    border: 1px solid #fed7aa;
  }
  .prof-legend-title {
    font-weight: 800;
    font-size: 0.98rem;
    color: #7c2d12;
    line-height: 1.2;
  }
  .prof-legend-hint {
    color: #a8a29e;
    font-size: 0.74rem;
  }

  .prof-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.76rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #78716c;
    margin-bottom: 6px;
  }
  .prof-req { color: #dc2626; }
  .prof-locked {
    margin-left: auto;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.66rem;
    font-weight: 600;
    color: #a8a29e;
  }

  /* Icon-prefixed inputs */
  .prof-input {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #ece7df;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .prof-input .input-group-text {
    background: #fff7ed;
    border: none;
    color: #c2410c;
    font-size: 0.95rem;
    padding-left: 14px;
    padding-right: 12px;
  }
  .prof-input .form-control,
  .prof-input .form-select {
    border: none;
    padding: 11px 14px;
    box-shadow: none !important;
    background: #fff;
  }
  .prof-input:focus-within {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234, 88, 12, 0.14);
  }
  .prof-input.is-locked,
  .prof-input.is-locked .form-control,
  .prof-input.is-locked .input-group-text {
    background: #f8f6f2;
    color: #78716c;
  }
  .prof-input.is-locked:focus-within {
    border-color: #ece7df;
    box-shadow: none;
  }
  .prof-textarea {
    border-radius: 12px;
    border: 1px solid #ece7df;
    padding: 11px 14px;
  }
  .prof-textarea:focus {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234, 88, 12, 0.14);
  }
  .prof-help {
    display: block;
    margin-top: 5px;
    font-size: 0.7rem;
    color: #a8a29e;
  }

  /* Action bar */
  .prof-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 4px;
  }
  .prof-actions-note {
    font-size: 0.78rem;
    font-weight: 600;
    color: #78716c;
  }

  .prof-content-card .form-control,
  .prof-content-card .form-select {
    border-radius: 10px;
  }
  .prof-content-card .form-control:focus,
  .prof-content-card .form-select:focus {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234, 88, 12,0.15);
  }
  .prof-save-btn {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #16a34a, #15803d);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .prof-save-btn:hover {
    background: linear-gradient(135deg, #15803d, #166534);
    transform: translateY(-2px);
    box-shadow: 0 12px 26px rgba(22,163,74,0.4);
  }

  /* Buy Again */
  .prof-buy-section {
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f6fdf9 100%);
  }

  /* Shelf header */
  .prof-buy-hero {
    position: relative;
    overflow: hidden;
    border-radius: 18px;
    padding: clamp(16px, 2.4vw, 24px);
    color: #fff;
    background: linear-gradient(120deg, #052e16, #065f46 45%, #047857 70%, #10b981);
    background-size: 220% 220%;
    animation: profDrift 18s ease-in-out infinite;
    box-shadow: 0 16px 34px rgba(4,120,87,0.28);
  }
  .prof-buy-hero-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(56px);
    pointer-events: none;
  }
  .prof-buy-hero-glow-1 {
    width: 190px; height: 190px;
    background: #34d399;
    opacity: 0.45;
    top: -90px; left: 12%;
  }
  .prof-buy-hero-glow-2 {
    width: 160px; height: 160px;
    background: #fbbf24;
    opacity: 0.3;
    bottom: -90px; right: 6%;
  }
  .prof-buy-hero-inner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }
  .prof-buy-hero-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 54px;
    height: 54px;
    border-radius: 16px;
    font-size: 1.6rem;
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(255,255,255,0.28);
    backdrop-filter: blur(6px);
    animation: profBuyFloat 4.5s ease-in-out infinite;
  }
  @keyframes profBuyFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-5px); }
  }
  .prof-buy-hero-title {
    font-size: clamp(1.25rem, 2.6vw, 1.6rem);
    font-weight: 800;
    letter-spacing: -0.3px;
  }
  .prof-buy-hero-sub {
    font-size: 0.82rem;
    color: rgba(255,255,255,0.78);
    max-width: 34ch;
  }
  .prof-buy-hero-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .prof-buy-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 92px;
    padding: 9px 14px;
    border-radius: 14px;
    background: rgba(255,255,255,0.13);
    border: 1px solid rgba(255,255,255,0.22);
    backdrop-filter: blur(6px);
  }
  .prof-buy-stat-value {
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.1;
  }
  .prof-buy-stat-label {
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: rgba(255,255,255,0.72);
  }
  .prof-buy-stat.is-drop {
    background: rgba(248,113,113,0.22);
    border-color: rgba(254,202,202,0.45);
  }
  .prof-buy-stat.is-save {
    background: rgba(251,191,36,0.22);
    border-color: rgba(253,230,138,0.5);
  }

  /* Shelf filters */
  .prof-buy-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .prof-buy-filter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 30px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #475569;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  }
  .prof-buy-filter:hover {
    transform: translateY(-2px);
    border-color: rgba(16,185,129,0.5);
    box-shadow: 0 8px 18px rgba(4,120,87,0.14);
  }
  .prof-buy-filter.is-active {
    background: linear-gradient(135deg, #047857, #10b981);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 10px 20px rgba(4,120,87,0.3);
  }
  .prof-buy-filter-count {
    min-width: 20px;
    padding: 1px 6px;
    border-radius: 20px;
    background: #f1f5f9;
    color: #475569;
    font-size: 0.68rem;
    font-weight: 800;
    text-align: center;
  }
  .prof-buy-filter.is-active .prof-buy-filter-count {
    background: rgba(255,255,255,0.25);
    color: #fff;
  }

  /* Product cards */
  .prof-buy-card {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #eef2f0 !important;
    box-shadow: 0 6px 18px rgba(15,23,42,0.06);
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    /* Cards fade up in sequence so the shelf fills in rather than pops */
    animation: profBuyRise 0.5s ease both;
    animation-delay: calc(var(--i, 0) * 60ms);
  }
  @keyframes profBuyRise {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .prof-buy-card:hover {
    transform: translateY(-8px);
    border-color: rgba(16,185,129,0.45) !important;
    box-shadow: 0 26px 46px rgba(4,120,87,0.2) !important;
  }
  .prof-buy-card-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    z-index: 3;
    height: 4px;
    background: linear-gradient(90deg, #047857, #10b981, #a7f3d0);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }
  .prof-buy-card:hover .prof-buy-card-accent {
    transform: scaleX(1);
  }
  .prof-buy-card.is-deal .prof-buy-card-accent {
    background: linear-gradient(90deg, #dc2626, #f97316, #fbbf24);
  }
  .prof-buy-img-wrap {
    position: relative;
    overflow: hidden;
    background: linear-gradient(160deg, #f8fafc, #ecfdf5);
  }
  .prof-buy-img {
    height: clamp(140px, 18vw, 180px);
    width: 100%;
    object-fit: contain;
    padding: 12px;
    transition: transform 0.45s ease;
  }
  .prof-buy-card:hover .prof-buy-img {
    transform: scale(1.09) rotate(-1deg);
  }
  /* Light sweeps across the photo on hover */
  .prof-buy-sheen {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%);
    transform: translateX(-120%);
  }
  .prof-buy-card:hover .prof-buy-sheen {
    animation: profBuySheen 0.9s ease;
  }
  @keyframes profBuySheen {
    to { transform: translateX(120%); }
  }
  .prof-buy-flags {
    position: absolute;
    top: 10px; left: 10px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  .prof-buy-flag {
    background: linear-gradient(135deg, #047857, #10b981);
    color: #fff;
    font-size: 0.66rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 30px;
    white-space: nowrap;
    box-shadow: 0 4px 10px rgba(4,120,87,0.32);
  }
  .prof-buy-flag-top {
    background: linear-gradient(135deg, #b45309, #f59e0b);
    box-shadow: 0 4px 12px rgba(180,83,9,0.35);
  }
  /* Price-drop ribbon */
  .prof-buy-drop {
    position: absolute;
    top: 10px; right: 10px;
    z-index: 2;
    background: linear-gradient(135deg, #dc2626, #f97316);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 30px;
    white-space: nowrap;
    box-shadow: 0 4px 10px rgba(220,38,38,0.32);
    animation: profDropPulse 2.4s ease-in-out infinite;
  }
  @keyframes profDropPulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.07); }
  }

  /* Sold-out treatment */
  .prof-buy-card.is-out { opacity: 0.85; }
  .prof-buy-card.is-out .prof-buy-img { filter: grayscale(0.6); }
  .prof-buy-out {
    position: absolute;
    inset: auto 0 0 0;
    z-index: 2;
    background: rgba(17,24,39,0.78);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    text-align: center;
    padding: 6px 0;
  }

  .prof-buy-meta {
    font-size: 0.72rem;
    color: #a8a29e;
    font-weight: 600;
  }

  /* Price movement vs what they last paid */
  .prof-buy-delta {
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.3;
  }
  .prof-buy-delta .is-up   { color: #b45309; }
  .prof-buy-delta .is-same { color: #a8a29e; font-weight: 600; }
  .prof-buy-save-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 30px;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #047857;
    font-weight: 800;
  }
  .prof-buy-stock {
    font-size: 0.7rem;
    font-weight: 700;
    color: #b45309;
  }
  .prof-buy-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    background: linear-gradient(to top, rgba(2,44,34,0.62), rgba(15,23,42,0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .prof-buy-card:hover .prof-buy-overlay {
    opacity: 1;
  }
  .prof-buy-overlay-btn {
    background: rgba(255,255,255,0.95);
    color: #111827;
    font-weight: 700;
    font-size: 0.78rem;
    padding: 7px 16px;
    border-radius: 50px;
    white-space: nowrap;
    transform: translateY(10px);
    transition: transform 0.3s ease;
  }
  .prof-buy-card:hover .prof-buy-overlay-btn {
    transform: translateY(0);
  }
  .prof-buy-name {
    font-size: 0.92rem;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .prof-buy-price {
    font-size: 1.15rem;
    background: linear-gradient(120deg, #047857, #10b981);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  /* Quantity + add-to-cart */
  .prof-buy-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .prof-buy-qty {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #f8fafc;
    overflow: hidden;
  }
  .prof-buy-qty button {
    width: 26px;
    height: 30px;
    border: none;
    background: transparent;
    color: #047857;
    font-size: 1rem;
    font-weight: 800;
    line-height: 1;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  .prof-buy-qty button:hover:not(:disabled) {
    background: #d1fae5;
  }
  .prof-buy-qty button:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
  }
  .prof-buy-qty-value {
    min-width: 20px;
    text-align: center;
    font-size: 0.82rem;
    font-weight: 800;
    color: #0f172a;
  }
  .prof-buy-btn {
    flex: 1 1 auto;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #047857, #10b981);
    box-shadow: 0 8px 18px rgba(4,120,87,0.28);
    white-space: nowrap;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .prof-buy-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #065f46, #059669);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(4,120,87,0.36);
  }
  .prof-buy-btn:disabled {
    background: #e7e5e4;
    color: #a8a29e;
    box-shadow: none;
    transform: none;
  }
  /* Brief confirmation right where the click happened */
  .prof-buy-btn.is-added,
  .prof-buy-btn.is-added:hover,
  .prof-buy-btn.is-added:focus,
  .prof-buy-btn.is-added:disabled {
    background: linear-gradient(135deg, #15803d, #22c55e);
    color: #fff;
    animation: profBuyPop 0.4s ease;
  }
  @keyframes profBuyPop {
    0%   { transform: scale(0.94); }
    55%  { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  .prof-buy-card.is-added {
    border-color: rgba(34,197,94,0.75) !important;
    box-shadow: 0 0 0 4px rgba(34,197,94,0.15), 0 20px 38px rgba(4,120,87,0.18) !important;
  }

  /* Nothing bought yet */
  .prof-buy-empty {
    padding: 34px 18px;
    border-radius: 18px;
    border: 1px dashed #a7f3d0;
    background: linear-gradient(180deg, #f0fdf4, #ffffff);
  }
  .prof-buy-empty-icon {
    font-size: 3rem;
    line-height: 1;
    animation: profBuyFloat 4.5s ease-in-out infinite;
  }
  .prof-buy-empty-btn {
    display: inline-block;
    padding: 9px 22px;
    border-radius: 30px;
    background: linear-gradient(135deg, #047857, #10b981);
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
    text-decoration: none;
    box-shadow: 0 10px 22px rgba(4,120,87,0.3);
    transition: transform 0.2s ease;
  }
  .prof-buy-empty-btn:hover {
    color: #fff;
    transform: translateY(-2px);
  }

  /* Two cards per row on phones leaves no width for a side-by-side row */
  @media (max-width: 575.98px) {
    .prof-buy-actions {
      flex-direction: column;
      align-items: stretch;
      gap: 6px;
    }
    .prof-buy-qty {
      justify-content: space-between;
    }
    .prof-buy-qty button { flex: 1 1 auto; }
  }

  @media (prefers-reduced-motion: reduce) {
    .prof-wrap::before, .prof-wrap::after, .prof-card::after,
    .prof-cover-blob, .prof-rays, .prof-stars, .prof-shine, .prof-spark,
    .prof-avatar-ring, .prof-halo, .prof-halo-ring, .prof-name,
    .prof-chip-done, .prof-content-card, .prof-buy-section,
    .prof-card, .prof-stat, .prof-stat::before, .prof-stat::after,
    .prof-chip, .prof-skel, .prof-buy-card, .prof-buy-img,
    .prof-mini-fill, .prof-quick-tile, .prof-input,
    .prof-buy-drop, .prof-save-btn, .prof-buy-btn,
    .prof-buy-hero, .prof-buy-hero-icon, .prof-buy-card-accent,
    .prof-buy-filter, .prof-buy-empty-icon, .prof-buy-empty-btn,
    .prof-buy-card:hover .prof-buy-sheen {
      transition: none;
      animation: none;
    }
  }
`;

export default ProfilePage;
